import React, { useState, useRef } from 'react';
import { User, TimeEntry, Expense } from '../types';
import { Icons } from '../constants';

interface Props {
  user: User;
  timeEntries: TimeEntry[];
  onAddTimeEntry: (entry: TimeEntry) => void;
  onUpdateTimeEntry: (entry: TimeEntry) => void;
  expenses: Expense[];
  onAddExpense: (expense: Expense) => void;
}

const EmployeeFinanceView: React.FC<Props> = ({ user, timeEntries, onAddTimeEntry, onUpdateTimeEntry, expenses, onAddExpense }) => {
  const [hours, setHours] = useState('');
  const [description, setDescription] = useState('');
  
  const [expenseAmount, setExpenseAmount] = useState('');
  const [expenseMerchant, setExpenseMerchant] = useState('');
  const [expenseNotes, setExpenseNotes] = useState('');
  const [expenseReceiptUrl, setExpenseReceiptUrl] = useState<string | undefined>();
  const receiptInputRef = useRef<HTMLInputElement>(null);

  // Mock data for employee
  const payPeriod = "Mar 1 - Mar 15, 2026";
  const myEntries = timeEntries.filter(t => t.userId === user.id);
  const hoursWorked = 84.5 + myEntries.filter(t => t.status === 'APPROVED').reduce((sum, entry) => sum + entry.hours, 0);
  const payRate = 28.50;
  const grossPay = hoursWorked * payRate;
  const bonuses = 150.00;
  const taxes = grossPay * 0.22; // Mock 22% tax rate
  const netPay = grossPay + bonuses - taxes;

  const handleLogTime = (e: React.FormEvent) => {
    e.preventDefault();
    if (!hours || isNaN(Number(hours))) return;
    
    const newEntry: TimeEntry = {
      id: Math.random().toString(36).substr(2, 9),
      userId: user.id,
      userName: user.name,
      date: new Date().toISOString(),
      hours: Number(hours),
      description,
      status: 'PENDING'
    };
    
    onAddTimeEntry(newEntry);
    setHours('');
    setDescription('');
  };

  const handleLogExpense = (e: React.FormEvent) => {
    e.preventDefault();
    if (!expenseAmount || isNaN(Number(expenseAmount)) || !expenseMerchant) return;

    const newExpense: Expense = {
      id: Math.random().toString(36).substr(2, 9),
      date: new Date().toISOString().split('T')[0],
      amount: Number(expenseAmount),
      merchant: expenseMerchant,
      category: 'General', // Default, could add a select if needed
      notes: expenseNotes,
      receiptUrl: expenseReceiptUrl
    };

    onAddExpense(newExpense);
    setExpenseAmount('');
    setExpenseMerchant('');
    setExpenseNotes('');
    setExpenseReceiptUrl(undefined);
  };

  return (
    <div className="space-y-8 animate-fade-in max-w-5xl mx-auto">
      <div className="bg-bark p-10 rounded-[40px] text-white flex flex-col md:flex-row justify-between items-center relative overflow-hidden shadow-xl">
        <div className="relative z-10">
          <h2 className="text-3xl font-black mb-2">My Compensation</h2>
          <p className="text-stone-400 font-medium text-lg">View your pay stubs, hours, and tax information.</p>
        </div>
        <div className="absolute right-0 top-0 opacity-5 transform translate-x-10 -translate-y-10 scale-150 pointer-events-none text-gold">
           <Icons.Finance />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Current Pay Period</p>
          <h3 className="text-2xl font-black text-bark mt-2">{payPeriod}</h3>
        </div>
        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Hours Logged</p>
          <h3 className="text-4xl font-black text-gecko mt-2">{hoursWorked.toFixed(1)} <span className="text-lg text-stone-400">hrs</span></h3>
        </div>
        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Estimated Net Pay</p>
          <h3 className="text-4xl font-black text-palm mt-2">${netPay.toFixed(2)}</h3>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <h3 className="text-2xl font-black text-bark mb-6">Log Time</h3>
          <form onSubmit={handleLogTime} className="space-y-4 mb-6">
            <div className="flex gap-4">
              <input
                type="number"
                step="0.5"
                placeholder="Hours"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                className="w-24 p-3 rounded-xl border border-stone-200 focus:ring-2 focus:ring-palm focus:border-palm outline-none"
                required
              />
              <input
                type="text"
                placeholder="Description (e.g., Oak pruning at Smith residence)"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="flex-1 p-3 rounded-xl border border-stone-200 focus:ring-2 focus:ring-palm focus:border-palm outline-none"
                required
              />
              <button type="submit" className="bg-palm text-white px-6 py-3 rounded-xl font-bold hover:bg-bark transition">
                Log
              </button>
            </div>
          </form>
          
          <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
            {myEntries.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center bg-stone-50 rounded-2xl border border-stone-100">
                <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-stone-300 mb-3 shadow-sm">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                </div>
                <p className="text-stone-500 font-medium text-sm">No recent time entries.</p>
                <p className="text-stone-400 text-xs mt-1">Log your hours above to see them here.</p>
              </div>
            ) : (
              myEntries.map(entry => (
                <div key={entry.id} className="flex justify-between items-center p-4 bg-stone-50 rounded-2xl border border-stone-100">
                  <div>
                    <p className="font-bold text-bark">{entry.description}</p>
                    <p className="text-xs text-stone-500">{entry.date}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-black text-gecko">{entry.hours} hrs</p>
                    <span className={`text-[10px] font-bold uppercase tracking-widest ${entry.status === 'APPROVED' ? 'text-palm' : entry.status === 'REJECTED' ? 'text-red-500' : 'text-amber-500'}`}>
                      {entry.status}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <h3 className="text-2xl font-black text-bark mb-6">Pay Breakdown</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-stone-50 rounded-2xl">
              <span className="font-bold text-stone-600">Regular Earnings ({hoursWorked.toFixed(1)} hrs @ ${payRate.toFixed(2)})</span>
              <span className="font-black text-bark">${grossPay.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-stone-50 rounded-2xl">
              <span className="font-bold text-stone-600">Bonuses / Allowances</span>
              <span className="font-black text-gecko">+${bonuses.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-stone-50 rounded-2xl">
              <span className="font-bold text-stone-600">Taxes & Deductions (Est.)</span>
              <span className="font-black text-red-500">-${taxes.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-palm/10 rounded-2xl border border-palm/20">
              <span className="font-black text-palm">Total Net Pay</span>
              <span className="font-black text-palm text-xl">${netPay.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm">
          <h3 className="text-2xl font-black text-bark mb-6">Tax Documents</h3>
          <div className="space-y-4">
            <button className="w-full flex items-center justify-between p-4 bg-stone-50 hover:bg-stone-100 transition rounded-2xl border border-stone-200 group">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-stone-400 shadow-sm group-hover:text-palm transition">
                  📄
                </div>
                <div className="text-left">
                  <p className="font-bold text-bark">2025 W-2 Form</p>
                  <p className="text-xs text-stone-500">Available Jan 31, 2026</p>
                </div>
              </div>
              <span className="text-palm font-bold text-sm">Download</span>
            </button>
            <button className="w-full flex items-center justify-between p-4 bg-stone-50 hover:bg-stone-100 transition rounded-2xl border border-stone-200 group">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-stone-400 shadow-sm group-hover:text-palm transition">
                  📄
                </div>
                <div className="text-left">
                  <p className="font-bold text-bark">Current W-4</p>
                  <p className="text-xs text-stone-500">Last updated: Jan 15, 2026</p>
                </div>
              </div>
              <span className="text-palm font-bold text-sm">Update</span>
            </button>
          </div>
        </div>
      </div>

      {/* Log Expense Section */}
      <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm mt-8">
        <h3 className="text-2xl font-black text-bark mb-6">Log Expense</h3>
        <form onSubmit={handleLogExpense} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Amount ($)</label>
              <input
                type="number"
                step="0.01"
                placeholder="0.00"
                value={expenseAmount}
                onChange={(e) => setExpenseAmount(e.target.value)}
                className="w-full p-4 bg-stone-50 rounded-xl border border-stone-200 focus:ring-2 focus:ring-palm focus:border-palm outline-none font-bold text-bark"
                required
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Merchant / Vendor</label>
              <input
                type="text"
                placeholder="e.g., Home Depot, Shell"
                value={expenseMerchant}
                onChange={(e) => setExpenseMerchant(e.target.value)}
                className="w-full p-4 bg-stone-50 rounded-xl border border-stone-200 focus:ring-2 focus:ring-palm focus:border-palm outline-none font-bold text-bark"
                required
              />
            </div>
          </div>
          
          <div>
            <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Description / Notes</label>
            <input
              type="text"
              placeholder="What was this for?"
              value={expenseNotes}
              onChange={(e) => setExpenseNotes(e.target.value)}
              className="w-full p-4 bg-stone-50 rounded-xl border border-stone-200 focus:ring-2 focus:ring-palm focus:border-palm outline-none text-bark"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-stone-500 uppercase tracking-widest mb-2">Receipt Image (Optional)</label>
            {expenseReceiptUrl ? (
              <div className="relative rounded-xl overflow-hidden border border-stone-200 bg-stone-50 h-32 flex items-center justify-center w-full md:w-1/2">
                <img src={expenseReceiptUrl} alt="Receipt Preview" className="h-full object-contain" />
                <button 
                  type="button"
                  onClick={() => setExpenseReceiptUrl(undefined)}
                  className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm shadow-md hover:bg-red-600 transition"
                >
                  ×
                </button>
              </div>
            ) : (
              <div className="w-full md:w-1/2 p-4 bg-stone-50 rounded-xl border border-stone-200 border-dashed flex flex-col items-center justify-center text-center">
                <input 
                  type="file" 
                  accept="image/*"
                  onChange={(e) => {
                    if (!e.target.files?.[0]) return;
                    const reader = new FileReader();
                    reader.onloadend = () => setExpenseReceiptUrl(reader.result as string);
                    reader.readAsDataURL(e.target.files[0]);
                  }}
                  className="hidden"
                  id="employee-receipt-upload"
                  ref={receiptInputRef}
                />
                <label 
                  htmlFor="employee-receipt-upload"
                  className="cursor-pointer text-gecko font-bold hover:text-palm transition"
                >
                  Click to browse or take a photo
                </label>
              </div>
            )}
          </div>

          <button type="submit" className="bg-palm text-white px-8 py-4 rounded-xl font-bold hover:bg-bark transition shadow-xl shadow-palm/20">
            Submit Expense
          </button>
        </form>
        
        {/* Recent Expenses List */}
        <div className="mt-8 pt-8 border-t border-stone-100">
          <h4 className="text-sm font-bold text-stone-500 uppercase tracking-widest mb-4">My Recent Expenses</h4>
          <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
            {expenses.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center bg-stone-50 rounded-2xl border border-stone-100">
                <p className="text-stone-500 font-medium text-sm">No recent expenses.</p>
              </div>
            ) : (
              expenses.map(expense => (
                <div key={expense.id} className="flex justify-between items-center p-4 bg-stone-50 rounded-2xl border border-stone-100">
                  <div>
                    <p className="font-bold text-bark">{expense.merchant}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <p className="text-xs text-stone-500">{expense.date}</p>
                      {expense.receiptUrl && (
                        <a 
                          href={expense.receiptUrl} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-[10px] text-palm font-bold hover:underline flex items-center"
                        >
                          <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path></svg>
                          Receipt
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-black text-red-500">${expense.amount.toFixed(2)}</p>
                    <span className="text-[10px] font-bold uppercase tracking-widest text-stone-400">
                      {expense.category}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Career Advancement Section */}
      <div className="bg-white p-8 rounded-[32px] border border-stone-200 shadow-sm mt-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h3 className="text-2xl font-black text-bark">Career Advancement</h3>
            <p className="text-stone-500 font-medium">Track your progress towards your next promotion.</p>
          </div>
          <div className="bg-stone-50 px-6 py-3 rounded-2xl border border-stone-200">
            <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Current Role</p>
            <p className="font-bold text-bark text-lg">Arborist Level II</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-6">
            <div className="bg-stone-50 p-6 rounded-2xl border border-stone-200">
              <div className="flex justify-between items-end mb-4">
                <div>
                  <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Target Role</p>
                  <h4 className="font-black text-xl text-bark">Lead Arborist</h4>
                </div>
                <p className="font-bold text-palm text-sm">75% Complete</p>
              </div>
              <div className="w-full bg-stone-200 rounded-full h-3 mb-6">
                <div className="bg-palm h-3 rounded-full" style={{ width: '75%' }}></div>
              </div>

              <div className="space-y-4">
                <h5 className="font-bold text-sm text-stone-500 uppercase tracking-widest">Requirements</h5>
                
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-gecko/20 flex items-center justify-center text-gecko">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-bark text-sm">ISA Certified Arborist Credential</p>
                    <p className="text-xs text-stone-500">Completed Oct 2025</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-gecko/20 flex items-center justify-center text-gecko">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-bark text-sm">2,000 Field Hours</p>
                    <p className="text-xs text-stone-500">2,150 hours logged</p>
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 rounded-full bg-stone-200 flex items-center justify-center text-stone-400">
                    <span className="font-black text-xs">3</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-bark text-sm">Crew Leadership Training</p>
                    <p className="text-xs text-stone-500">Scheduled for April 15, 2026</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-palm/10 p-6 rounded-2xl border border-palm/20">
              <h4 className="font-black text-palm mb-2">Next Steps</h4>
              <p className="text-sm text-bark font-medium mb-4">Complete your Crew Leadership Training to become eligible for the Lead Arborist promotion.</p>
              <button className="w-full py-3 bg-palm text-white rounded-xl font-bold shadow-lg shadow-palm/20 hover:bg-bark transition text-sm">
                View Training Schedule
              </button>
            </div>
            
            <div className="bg-stone-50 p-6 rounded-2xl border border-stone-200">
              <h4 className="font-black text-bark mb-2">Performance Review</h4>
              <p className="text-sm text-stone-500 mb-4">Your next annual review is scheduled for June 2026.</p>
              <button className="w-full py-3 bg-white text-bark border border-stone-200 rounded-xl font-bold hover:bg-stone-100 transition text-sm">
                View Past Reviews
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeFinanceView;